import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Modal } from 'react-bootstrap';
import BackButton from '../MiniComponent/BackButton';

const DashboardFilter = ({
    filterType,
    setFilterType,
    customStartDate,
    setCustomStartDate,
    customEndDate,
    setCustomEndDate,
    selectedRequirementId,
    setSelectedRequirementId,
    requirements,
    startTime,
    setStartTime,
    endTime,
    setEndTime,
    headerLeft,
    headerRight
}) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [showTimelineModal, setShowTimelineModal] = useState(false);
    const [localFilterType, setLocalFilterType] = useState(filterType);
    const [localStartDate, setLocalStartDate] = useState(customStartDate);
    const [localEndDate, setLocalEndDate] = useState(customEndDate);
    const [localStartTime, setLocalStartTime] = useState(startTime);
    const [localEndTime, setLocalEndTime] = useState(endTime);

    const getFilterTypeDisplayName = () => {
        const mapping = {
            'today': 'Today',
            'yesterday': 'Yesterday',
            'this_week': 'This Week',
            'last_week': 'Last Week',
            'this_month': 'This Month',
            'prev_month': 'Last Month',
            'all': 'Timeline',
            'all_time': 'Timeline',
            'timeline': 'Timeline',
            'custom': 'Custom Range'
        };
        return mapping[filterType] || 'Timeline';
    };

    const getCalculatedDateString = (type, bound) => {
        const today = new Date();
        const formatDate = (d) => {
            const y = d.getFullYear();
            const m = String(d.getMonth() + 1).padStart(2, '0');
            const day = String(d.getDate()).padStart(2, '0');
            return `${y}-${m}-${day}`;
        };

        if (type === 'today') {
            return formatDate(today);
        } else if (type === 'yesterday') {
            const yesterday = new Date();
            yesterday.setDate(today.getDate() - 1);
            return formatDate(yesterday);
        } else if (type === 'this_week') {
            const dayOfWeek = today.getDay();
            const startOfWeek = new Date(today);
            const diff = today.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
            startOfWeek.setDate(diff);
            if (bound === 'start') {
                return formatDate(startOfWeek);
            } else {
                const endOfWeek = new Date(startOfWeek);
                endOfWeek.setDate(startOfWeek.getDate() + 6);
                return formatDate(endOfWeek);
            }
        } else if (type === 'last_week') {
            const dayOfWeek = today.getDay();
            const startOfLastWeek = new Date(today);
            const diff = today.getDate() - dayOfWeek - 6;
            startOfLastWeek.setDate(diff);
            if (bound === 'start') {
                return formatDate(startOfLastWeek);
            } else {
                const endOfLastWeek = new Date(startOfLastWeek);
                endOfLastWeek.setDate(startOfLastWeek.getDate() + 6);
                return formatDate(endOfLastWeek);
            }
        } else if (type === 'this_month') {
            if (bound === 'start') {
                return formatDate(new Date(today.getFullYear(), today.getMonth(), 1));
            } else {
                return formatDate(new Date(today.getFullYear(), today.getMonth() + 1, 0));
            }
        } else if (type === 'prev_month' || type === 'last_month') {
            if (bound === 'start') {
                return formatDate(new Date(today.getFullYear(), today.getMonth() - 1, 1));
            } else {
                return formatDate(new Date(today.getFullYear(), today.getMonth(), 0));
            }
        } else if (type === 'all' || type === 'all_time' || type === 'timeline') {
            if (bound === 'start') {
                return '2000-01-01';
            } else {
                return '2100-01-01';
            }
        }
        return '';
    };

    const handleCustomDateChange = (isStart, val) => {
        setLocalFilterType('custom');
        if (isStart) {
            setLocalStartDate(val);
        } else {
            setLocalEndDate(val);
        }
    };

    useEffect(() => {
        if (showTimelineModal) {
            setLocalFilterType(filterType);
            setLocalStartDate(customStartDate || getCalculatedDateString(filterType, 'start'));
            setLocalEndDate(customEndDate || getCalculatedDateString(filterType, 'end'));
            setLocalStartTime(startTime);
            setLocalEndTime(endTime);
        }
    }, [showTimelineModal, filterType, customStartDate, customEndDate, startTime, endTime]);

    return (
        <div className="bg-white border border-slate-100 rounded-2xl p-3 shadow-sm mb-0 font-popins">
            <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-2 flex-wrap">
                <div className="flex flex-wrap items-center gap-2">
                    <BackButton />
                    <h4 className="text-lg font-bold text-slate-800 m-0 whitespace-nowrap">Activity Dashboard</h4>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                    
                    {headerLeft}
                    {/* Requirement Dropdown Select */}
                    <div className="flex items-center gap-1 bg-slate-50 border border-slate-200 rounded-xl px-2 py-1.5 shadow-sm">
                        <span className="text-[12px] text-slate-600 font-bold uppercase tracking-wider">Requirement</span>
                        <select
                            className="border-0 bg-transparent text-sm text-slate-700 outline-none focus:ring-0 py-0 cursor-pointer max-w-[150px]"
                            value={selectedRequirementId}
                            onChange={(e) => setSelectedRequirementId(e.target.value)}
                        >
                            <option value="">All Requirements</option>
                            {requirements && requirements.map((req) => (
                                <option key={req.assigned_requirement} value={req.assigned_requirement}>
                                    {req.requirement_name || `${req.client_name} - ${req.job_title}`}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Timeline Filter Toggle Button */}
                    <button
                        onClick={() => setShowTimelineModal(true)}
                        className="flex items-center gap-1 bg-slate-50 border border-slate-200 rounded-xl px-2 py-1.5 shadow-sm hover:border-slate-300 transition-all duration-150 focus:outline-none"
                        title="Timeline Selection"
                    >
                        <i className="fas fa-sliders-h text-indigo-600 text-[11px]"></i>
                        <span className="text-[12px] text-slate-600 font-bold uppercase tracking-wider poppins">Timeline:</span>
                        <span className="text-sm font-semibold text-indigo-700 whitespace-nowrap">{getFilterTypeDisplayName()}</span>
                    </button>

                    {headerRight}
                </div>
            </div>

            {showTimelineModal && (
                <Modal
                    show={showTimelineModal}
                    onHide={() => setShowTimelineModal(false)}
                    centered
                    dialogClassName="max-w-[420px]"
                >
                    <div className="bg-white rounded-2xl overflow-hidden shadow-2xl p-6 border border-slate-100 poppins">
                        {/* Header */}
                        <div className="flex justify-between items-start mb-4">
                            <div>
                                <h3 className="text-xl font-bold text-slate-800 m-0">Timeline Selection</h3>
                                <span className="text-[10px] font-bold text-indigo-600 uppercase tracking-widest block mt-1">
                                    Currently: {getFilterTypeDisplayName().toUpperCase()}
                                </span>
                            </div>
                            <button
                                onClick={() => setShowTimelineModal(false)}
                                className="text-slate-400 hover:text-slate-600 bg-slate-50 hover:bg-slate-100 rounded-full w-7 h-7 flex items-center justify-center transition-all border-0 focus:outline-none"
                            >
                                <i className="fas fa-times"></i>
                            </button>
                        </div>

                        {/* Presets Grid */}
                        <div className="grid grid-cols-3 gap-2 mb-4">
                            {[
                                { code: 'today', label: 'Today' },
                                { code: 'yesterday', label: 'Yesterday' },
                                { code: 'this_week', label: 'This Week' },
                                { code: 'last_week', label: 'Last Week' },
                                { code: 'this_month', label: 'This Month' },
                                { code: 'prev_month', label: 'Last Month' },
                                { code: 'all', label: 'Timeline' }
                            ].map((preset) => {
                                const isActive = localFilterType === preset.code || (preset.code === 'all' && (localFilterType === 'all_time' || localFilterType === 'timeline'));
                                return (
                                    <button
                                        key={preset.code}
                                        onClick={() => {
                                            setLocalFilterType(preset.code);
                                            setLocalStartDate(getCalculatedDateString(preset.code, 'start'));
                                            setLocalEndDate(getCalculatedDateString(preset.code, 'end'));
                                        }}
                                        className={`py-2 text-[11px] font-bold uppercase tracking-wider rounded-xl transition-all duration-200 border text-center ${preset.code === 'all' ? 'col-span-3' : ''
                                            } ${isActive
                                                ? 'bg-indigo-600 border-indigo-600 text-white shadow-md shadow-indigo-100'
                                                : 'bg-slate-50 border-slate-200 text-slate-700 hover:bg-indigo-50 hover:text-indigo-600 hover:border-indigo-200'
                                            }`}
                                    >
                                        {preset.label}
                                    </button>
                                );
                            })}
                        </div>

                        {/* Divider */}
                        <hr className="border-slate-100 my-4" />

                        {/* Flux Range Section */}
                        <div className="mb-4">
                            <div className="flex justify-between items-center mb-3">
                                <h4 className="text-xs font-bold text-slate-800 uppercase tracking-widest m-0">Flux Range</h4>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setLocalFilterType('this_month');
                                        setLocalStartDate(getCalculatedDateString('this_month', 'start'));
                                        setLocalEndDate(getCalculatedDateString('this_month', 'end'));
                                        setLocalStartTime(null);
                                        setLocalEndTime(null);
                                    }}
                                    className="text-[11px] font-bold text-rose-500 hover:text-rose-700 bg-transparent border-0 outline-none p-0 cursor-pointer uppercase tracking-wider transition-colors"
                                >
                                    Reset
                                </button>
                            </div>

                            <div className="grid grid-cols-2 gap-3 mb-3">
                                <div className="flex flex-col gap-1">
                                    <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Start Date</label>
                                    <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-2 focus-within:border-indigo-500 focus-within:ring-1 focus-within:ring-indigo-500 transition-all">
                                        <input
                                            type="date"
                                            value={localStartDate}
                                            onChange={(e) => handleCustomDateChange(true, e.target.value)}
                                            className="border-0 bg-transparent text-xs text-slate-700 font-semibold outline-none focus:ring-0 p-0 w-full"
                                        />
                                    </div>
                                </div>
                                <div className="flex flex-col gap-1">
                                    <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">End Date</label>
                                    <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-2 focus-within:border-indigo-500 focus-within:ring-1 focus-within:ring-indigo-500 transition-all">
                                        <input
                                            type="date"
                                            value={localEndDate}
                                            onChange={(e) => handleCustomDateChange(false, e.target.value)}
                                            className="border-0 bg-transparent text-xs text-slate-700 font-semibold outline-none focus:ring-0 p-0 w-full"
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-3">
                                <div className="flex flex-col gap-1">
                                    <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Start Time</label>
                                    <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-2 focus-within:border-indigo-500 focus-within:ring-1 focus-within:ring-indigo-500 transition-all">
                                        <input
                                            type="time"
                                            value={localStartTime || ''}
                                            onChange={(e) => setLocalStartTime(e.target.value || null)}
                                            className="border-0 bg-transparent text-xs text-slate-700 font-semibold outline-none focus:ring-0 p-0 w-full"
                                        />
                                    </div>
                                </div>
                                <div className="flex flex-col gap-1">
                                    <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">End Time</label>
                                    <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-2 focus-within:border-indigo-500 focus-within:ring-1 focus-within:ring-indigo-500 transition-all">
                                        <input
                                            type="time"
                                            value={localEndTime || ''}
                                            onChange={(e) => setLocalEndTime(e.target.value || null)}
                                            className="border-0 bg-transparent text-xs text-slate-700 font-semibold outline-none focus:ring-0 p-0 w-full"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Actions */}
                        <button
                            onClick={() => {
                                const newParams = new URLSearchParams(searchParams);
                                newParams.set('filter_type', localFilterType);
                                if (localFilterType === 'custom') {
                                    newParams.set('start_date', localStartDate);
                                    newParams.set('end_date', localEndDate);
                                } else {
                                    newParams.delete('start_date');
                                    newParams.delete('end_date');
                                }
                                if (localStartTime) {
                                    newParams.set('start_time', localStartTime);
                                } else {
                                    newParams.delete('start_time');
                                }
                                if (localEndTime) {
                                    newParams.set('end_time', localEndTime);
                                } else {
                                    newParams.delete('end_time');
                                }
                                setSearchParams(newParams);
                                setShowTimelineModal(false);
                            }}
                            className="w-full py-3 bg-[#2563EB] hover:bg-[#004ef8] text-white font-bold text-xs rounded-xl tracking-widest transition-all duration-200 uppercase mt-2 shadow-lg hover:shadow-xl active:scale-[0.98]"
                        >
                            Sync Range
                        </button>
                    </div>
                </Modal>
            )}
        </div>
    );
};

export default DashboardFilter;